/**
 * Non-Redux token store — the transport read source for the primary-session JWT
 * bearer (auth-redux-elimination, slice-2 keystone).
 *
 * It holds the five auth-token fields that today live on `state.auth`. The
 * transport request-config boundary (`axios.selectors`, consumed by the
 * request-config helper) sources the primary-session Authorization bearer from
 * here; on the browser Redux only MIRRORS into it (see `tokenStore.interop.ts`).
 *
 * ## Request scoping (browser vs. server)
 *
 * On the BROWSER there is exactly one user per JS runtime, so the tokens live in a
 * module singleton — correct and idiomatic.
 *
 * On the SERVER a single Node process multiplexes EVERY concurrent SSR request, so
 * a module singleton cannot represent per-request auth: one request could overwrite
 * the global bearer while another's render is still reading it, leaking one user's
 * Authorization into another's response. To keep the SAME read API isomorphic, all
 * reads and writes route through a pluggable *context resolver*. The server installs
 * an AsyncLocalStorage-backed resolver (see `tokenStore.server.ts`) that returns the
 * current request's token context; the browser installs nothing, so reads fall
 * through to the module singleton. The resolver is the ONLY thing that differs
 * between runtimes — this module stays browser-safe (no `async_hooks` import).
 *
 * Hydration: this store is NOT hydrated from cookies at module-load time. On the
 * browser the Redux write-through primes from the SSR-seeded initial state at boot,
 * then mirrors every subsequent auth-token change via a Redux middleware (see
 * `tokenStore.interop.ts`). On the server the per-request context is seeded directly
 * from the request cookies before the first authed request is built
 * (`runWithTokenContext` in `src/server.ts`). See the `auth-redux-migration`
 * skill reference for the SSR request-scoping STOP condition.
 *
 * Reactivity: this is a minimal reactive store — `subscribe(listener)` +
 * `getSnapshot()` are useSyncExternalStore-compatible. `getSnapshot()` returns a
 * referentially-stable `AuthTokens` snapshot whose identity changes ONLY when a
 * token field actually changes: `setTokens` / `clear` diff before replacing the
 * snapshot and notifying, so a `useSyncExternalStore` subscriber does not loop.
 * Reactivity is a BROWSER concern — one JS runtime, one user, one module context;
 * the server never subscribes (SSR renders once via a server snapshot). No consumer
 * subscribes yet (this is a pure seam): App.tsx still reads the mirrored Redux
 * `useSelector` values to drive its effects. Do NOT place a bare `tokenStore.getX()`
 * in a React dependency array — subscribe to the snapshot instead.
 */

export type AuthTokens = {
  jwt: string | undefined
  refreshToken: string | undefined
  cspJwt: string | undefined
  cspRefreshToken: string | undefined
  userSessionTokenId: string | undefined
}

const emptyTokens = (): AuthTokens => ({
  jwt: undefined,
  refreshToken: undefined,
  cspJwt: undefined,
  cspRefreshToken: undefined,
  userSessionTokenId: undefined,
})

/**
 * A mutable holder for one logical session's tokens. The browser uses exactly one
 * (the module singleton below); the server creates one per request inside its
 * AsyncLocalStorage context.
 */
export type TokenContext = { tokens: AuthTokens }

/**
 * Browser / default context. On the server this is never written — every request
 * resolves to its own context — so a stray uncontextualized server read fails closed
 * to "no bearer" rather than leaking another request's token.
 */
const moduleContext: TokenContext = { tokens: emptyTokens() }

/**
 * Resolver indirection. The default returns `undefined` (browser, or no request
 * context active), so reads fall through to `moduleContext`. The server overrides
 * this once at boot via `setTokenContextResolver`.
 */
let resolveContext: () => TokenContext | undefined = () => undefined

/**
 * Install a request-scoped token-context resolver. The server calls this once at
 * boot with an AsyncLocalStorage-backed resolver so concurrent requests each
 * read/write their own tokens. The browser never calls it.
 */
export const setTokenContextResolver = (
  resolver: () => TokenContext | undefined,
): void => {
  resolveContext = resolver
}

/**
 * The active context for the current execution: the per-request context on the
 * server, the module singleton on the browser (or whenever no request context is
 * active).
 */
const current = (): TokenContext => resolveContext() ?? moduleContext

/**
 * Browser subscribers (a `useSyncExternalStore` on-change registry). Reactivity is
 * a browser-only concern — one runtime, one user — so the set is module-global; the
 * server never registers a listener.
 */
const listeners = new Set<() => void>()

const notify = (): void => {
  listeners.forEach((listener) => listener())
}

const tokensEqual = (a: AuthTokens, b: AuthTokens): boolean =>
  a.jwt === b.jwt &&
  a.refreshToken === b.refreshToken &&
  a.cspJwt === b.cspJwt &&
  a.cspRefreshToken === b.cspRefreshToken &&
  a.userSessionTokenId === b.userSessionTokenId

const isEmpty = (t: AuthTokens): boolean =>
  t.jwt === undefined &&
  t.refreshToken === undefined &&
  t.cspJwt === undefined &&
  t.cspRefreshToken === undefined &&
  t.userSessionTokenId === undefined

export const tokenStore = {
  /** Primary-session JWT bearer (today `state.auth.jwt`). */
  getAccessToken: (): string | undefined => current().tokens.jwt,
  getRefreshToken: (): string | undefined => current().tokens.refreshToken,
  getCspJwt: (): string | undefined => current().tokens.cspJwt,
  getCspRefreshToken: (): string | undefined =>
    current().tokens.cspRefreshToken,
  getUserSessionTokenId: (): string | undefined =>
    current().tokens.userSessionTokenId,

  /**
   * Subscribe to token changes (useSyncExternalStore-compatible). Returns an
   * unsubscribe handle. Listeners fire only when `setTokens` / `clear` actually
   * change a field — a no-op write does not notify.
   */
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },

  /**
   * The current token snapshot for the active context (useSyncExternalStore
   * `getSnapshot`). Referentially stable: the same object is returned until a field
   * changes, so a subscriber can compare by identity and does not re-render on
   * no-op writes.
   */
  getSnapshot: (): AuthTokens => current().tokens,

  /**
   * Replace the full token snapshot for the active context. On the browser the
   * Redux write-through calls this with the current `state.auth` token fields on
   * every change, so the store always equals Redux. Full-snapshot (not per-field)
   * semantics keep mirror writes total: a field that becomes `undefined` in Redux
   * is cleared here too. A write that changes nothing is a no-op (snapshot identity
   * is preserved and no listener fires).
   */
  setTokens: (next: AuthTokens): void => {
    const context = current()
    const nextTokens: AuthTokens = {
      jwt: next.jwt,
      refreshToken: next.refreshToken,
      cspJwt: next.cspJwt,
      cspRefreshToken: next.cspRefreshToken,
      userSessionTokenId: next.userSessionTokenId,
    }
    if (tokensEqual(context.tokens, nextTokens)) {
      return
    }
    context.tokens = nextTokens
    notify()
  },

  /** Reset every field to `undefined` for the active context (no-op if already empty). */
  clear: (): void => {
    const context = current()
    if (isEmpty(context.tokens)) {
      return
    }
    context.tokens = emptyTokens()
    notify()
  },
}
